import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { formatApiErrorDetail, setStoredToken, getStoredToken } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // user object | null
  const [tenant, setTenant] = useState(null);
  const [checking, setChecking] = useState(true);

  const refreshMe = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data.user);
      setTenant(data.tenant || null);
      return data;
    } catch (e) {
      setUser(null);
      setTenant(null);
      return null;
    }
  }, []);

  useEffect(() => {
    (async () => {
      // Only call /me if we have a stored token OR a same-origin cookie
      // (we still try blindly because the cookie may exist even without LS token)
      await refreshMe();
      setChecking(false);
    })();
  }, [refreshMe]);

  const login = async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      // Persist token so subsequent requests include Authorization header
      // (works across preview domains where cross-origin cookies are blocked).
      if (data?.access_token) {
        setStoredToken(data.access_token);
      }
      setUser(data.user);
      // Now hydrate full /me payload (includes tenant + enabled_modules)
      await refreshMe();
      return {
        ok: true,
        user: data.user,
        redirect_url: data.redirect_url || null,
      };
    } catch (e) {
      console.error("[auth.login] failed", e);
      return {
        ok: false,
        error: formatApiErrorDetail(e?.response?.data?.detail) || e.message || "Login failed",
        status: e?.response?.status,
      };
    }
  };

  /**
   * Phase 11.5 — Post-Stripe onboarding "claim password" flow.
   *
   * MUST hydrate the auth context BEFORE the caller navigates, otherwise
   * route guards will bounce the user to /login (or worse, to the public
   * marketing home). We:
   *   1. Hit /auth/claim-account
   *   2. Persist the access_token in localStorage
   *   3. Optimistically set user/tenant from the response payload
   *   4. Run refreshMe() to confirm and pick up any server-side defaults
   *   5. Return the server-computed redirect_url so the caller can navigate
   */
  const claimAccount = async ({ email, token, new_password }) => {
    try {
      const { data } = await api.post("/auth/claim-account", {
        email: (email || "").trim().toLowerCase(),
        token,
        new_password,
      });
      if (data?.access_token) {
        setStoredToken(data.access_token);
      }
      if (data?.user) setUser(data.user);
      if (data?.tenant) setTenant(data.tenant);
      // Refresh from /me to ensure subsequent module/access matrix calls work
      await refreshMe();
      return {
        ok: true,
        user: data.user,
        tenant: data.tenant,
        redirect_url: data.redirect_url || null,
      };
    } catch (e) {
      console.error("[auth.claimAccount] failed", e);
      return {
        ok: false,
        error:
          formatApiErrorDetail(e?.response?.data?.detail)
          || e.message
          || "Activation failed",
        status: e?.response?.status,
      };
    }
  };

  const register = async (email, password, name) => {
    try {
      const { data } = await api.post("/auth/register", { email, password, name });
      if (data?.access_token) {
        setStoredToken(data.access_token);
      }
      setUser(data.user);
      await refreshMe();
      return { ok: true, user: data.user };
    } catch (e) {
      console.error("[auth.register] failed", e);
      return {
        ok: false,
        error: formatApiErrorDetail(e?.response?.data?.detail) || e.message || "Registration failed",
        status: e?.response?.status,
      };
    }
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (_) {}
    setStoredToken(null);
    setUser(null);
    setTenant(null);
  };

  const isPlatform =
    user && (user.role === "platform_owner" || user.role === "platform_admin");

  return (
    <AuthContext.Provider
      value={{
        user,
        tenant,
        checking,
        isPlatform,
        login,
        register,
        logout,
        refreshMe,
        claimAccount,
        hasStoredToken: !!getStoredToken(),
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}
